// ─────────────────────────────────────────────────────────────────────────────
// buildNodeDetail.js — turns an enriched-node row into the view-model the detail
// modal renders. Every field below is read from the row; nothing is synthesised.
//
// Two things used to be invented here and are now gone:
//
//   • CPU Usage and Memory were derived from a hash of the node_id. Being
//     deterministic they looked stable and real across reloads — the worst kind
//     of fake. node_metrics has no CPU or memory column, so there is no field
//     coming. The bars are removed rather than dashed: a progress bar sitting at
//     "—" is still a bar, and still implies a measurement exists.
//
//   • The Logs tab rendered ten hardcoded lines. It now streams the node's real
//     logs over SSE — see tabs/LogsTab.js and hooks/useNodeLogs.js.
//
// Bandwidth survives because avg_network_load_bps is real, and its bar is only
// emitted when the node actually reported one.
// ─────────────────────────────────────────────────────────────────────────────
import { DASH, dash, fmtLatency, fmtUptime, fmtBandwidth, fmtPercent } from "../../format";
import { STATUS_CONFIG } from "../badges";

// Nominal per-node ceiling, used only to scale the bandwidth bar to a width.
// Not a reading, and never displayed as one.
const BANDWIDTH_MAX_BPS = 10_000_000_000; // 10 Gbps

const clampPct = (n) => Math.max(0, Math.min(100, Math.round(n)));

export function buildNodeDetail(node) {
  if (!node) return null;

  const status = node.status || "Offline";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Offline;

  // Only a node that reported throughput gets a bandwidth bar.
  const bps = node.avg_network_load_bps;
  const metrics = Number.isFinite(bps)
    ? [{
        key: "bandwidth",
        icon: "wifi",
        label: "Bandwidth",
        value: fmtBandwidth(bps),
        pct: clampPct((bps / BANDWIDTH_MAX_BPS) * 100),
      }]
    : [];

  return {
    // ── Header ──
    id: dash(node.node_id),
    region: node.region || node.country || DASH,
    status,
    statusLabel: status.toUpperCase(),
    statusColor: cfg.color,
    statusBg: cfg.bg,
    statusBorder: cfg.border,

    // ── Overview: two featured cards (icon + big value) ──
    featured: [
      { key: "uptime", icon: "pulse", label: "Uptime", value: fmtUptime(node.uptime_seconds) },
      { key: "ip", icon: "shield", label: "IP Address", value: dash(node.ip), mono: true },
    ],

    // ── Overview: four compact stat rows (label ↔ value) ──
    stats: [
      { key: "latency", label: "Latency",         value: fmtLatency(node.latency_avg_ms) },
      { key: "load",    label: "Load",            value: fmtPercent(node.load_percent) },
      { key: "trust",   label: "Trust Score",     value: dash(node.trust_score) },
      { key: "users",   label: "Connected Users", value: dash(node.active_circuits) },
    ],

    // ── Overview: SYSTEM METRICS bars (empty → section not rendered) ──
    metrics,

    // ── Alerts tab ──
    alerts: Array.isArray(node.alerts) ? node.alerts : [],
  };
}
